import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Color palettes
export const COLOR_PALETTES = {
  default: {
    name: 'Classic Gold',
    gold: '#C4A052',
    goldSoft: '#E5C86B',
    primary: '#C4A052',
    accent: '#B8941F',
  },
  rose: {
    name: 'Rose Blush',
    gold: '#E8A4A4',
    goldSoft: '#F5C6C6',
    primary: '#D4758A',
    accent: '#C45B7C',
  },
  ocean: {
    name: 'Ocean Blue',
    gold: '#5B9BD5',
    goldSoft: '#8FBAE8',
    primary: '#3D7EBF',
    accent: '#2563EB',
  },
  emerald: {
    name: 'Emerald Green',
    gold: '#34D399',
    goldSoft: '#6EE7B7',
    primary: '#059669',
    accent: '#047857',
  },
  lavender: {
    name: 'Lavender Dream',
    gold: '#A78BFA',
    goldSoft: '#C4B5FD',
    primary: '#8B5CF6',
    accent: '#7C3AED',
  },
  sunset: {
    name: 'Sunset Orange',
    gold: '#FB923C',
    goldSoft: '#FDBA74',
    primary: '#F97316',
    accent: '#EA580C',
  },
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('skinuse_darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [colorPalette, setColorPalette] = useState(() => {
    const saved = localStorage.getItem('skinuse_colorPalette');
    return saved || 'default';
  });

  // Apply theme changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Dark mode
    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('skinuse_darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Apply color palette
  useEffect(() => {
    const root = document.documentElement;
    const palette = COLOR_PALETTES[colorPalette] || COLOR_PALETTES.default;
    
    root.style.setProperty('--gold', palette.gold);
    root.style.setProperty('--gold-soft', palette.goldSoft);
    root.style.setProperty('--primary-accent', palette.primary);
    root.style.setProperty('--accent-color', palette.accent);
    
    localStorage.setItem('skinuse_colorPalette', colorPalette);
  }, [colorPalette]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  const value = {
    darkMode,
    setDarkMode,
    toggleDarkMode,
    colorPalette,
    setColorPalette,
    palettes: COLOR_PALETTES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
